import { useNavigate } from 'react-router-dom';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button,
    Box, Typography, Card, CardContent, CardActions, Avatar, Chip, useTheme
} from '@mui/material';
import { 
    ArrowForwardIos as ArrowForwardIosIcon, 
    Visibility as VisibilityIcon 
} from '@mui/icons-material';

const TeacherList = ({ teachers }) => {
    const navigate = useNavigate();
    const theme = useTheme();

    const handleViewDetails = (teacher) => {
        // Pass teacher data via state to avoid an extra fetch on the detail page for the initial load
        navigate(`/admin/teacher/${teacher.id}`, { state: { teacher } });
    };

    if (!teachers || teachers.length === 0) {
        return (
            <Paper sx={{ textAlign: 'center', p: 6, borderRadius: 3, boxShadow: 'none', border: '1px dashed', borderColor: 'divider' }}>
                <Typography variant="h6" color="text.primary" gutterBottom>No Pending Applications</Typography>
                <Typography color="text.secondary">All applications have been reviewed.</Typography>
            </Paper>
        );
    }

    // Updated to handle both date and time beautifully
    const formatDateTime = (timestamp) => {
        if (!timestamp || !timestamp._seconds) {
            return { date: 'N/A', time: '' };
        }
        const dateObj = new Date(timestamp._seconds * 1000);
        return {
            date: dateObj.toLocaleDateString("en-US", {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            }),
            time: dateObj.toLocaleTimeString("en-US", {
                hour: '2-digit',
                minute: '2-digit'
            })
        };
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* TABLE VIEW: Visible on medium screens and up (md) */}
            <TableContainer 
                component={Paper} 
                sx={{ 
                    display: { xs: 'none', md: 'block' },
                    borderRadius: 3,
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.03)',
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden'
                }}
            >
                <Table sx={{ minWidth: 650 }} aria-label="pending teachers table">
                    <TableHead sx={{ bgcolor: 'grey.50' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Subjects</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Hourly Rate</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Applied On</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {teachers.map((teacher) => {
                            const dateTime = formatDateTime(teacher.createdAt);
                            
                            return (
                                <TableRow 
                                    hover 
                                    key={teacher.id} 
                                    sx={{ 
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        transition: 'background-color 0.2s',
                                    }}
                                >
                                    <TableCell component="th" scope="row">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar 
                                                src={teacher.photoURL} 
                                                alt={teacher.displayName} 
                                                sx={{ width: 40, height: 40, border: '1px solid', borderColor: 'divider' }}
                                            />
                                            <Typography variant="body1" fontWeight="500" color="text.primary">
                                                {teacher.displayName}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {teacher.subjects?.length > 0 ? (
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {teacher.subjects.slice(0, 3).map(sub => (
                                                    <Chip key={sub} label={sub} size="small" variant="outlined" sx={{ borderRadius: 1 }} />
                                                ))}
                                                {teacher.subjects.length > 3 && (
                                                    <Chip label={`+${teacher.subjects.length - 3}`} size="small" variant="outlined" sx={{ borderRadius: 1 }} />
                                                )}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">N/A</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="500">
                                            {teacher.hourlyRate ? `$${teacher.hourlyRate}/hr` : <span style={{ color: theme.palette.text.disabled }}>N/A</span>}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {/* Date and Time stacked neatly for the table view */}
                                        <Typography variant="body2" color="text.primary" sx={{ display: 'block' }}>
                                            {dateTime.date}
                                        </Typography>
                                        {dateTime.time && (
                                            <Typography variant="caption" color="text.secondary">
                                                {dateTime.time}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<VisibilityIcon />}
                                            onClick={() => handleViewDetails(teacher)}
                                            sx={{ 
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                fontWeight: 600
                                            }}
                                        >
                                            Review
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* CARD VIEW: Visible on extra-small screens and up (xs), hidden on md and up */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {teachers.map((teacher) => {
                        const dateTime = formatDateTime(teacher.createdAt);
                        
                        return (
                            <Card 
                                key={teacher.id} 
                                elevation={0}
                                sx={{ 
                                    borderRadius: 3, 
                                    border: '1px solid', 
                                    borderColor: 'divider',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                            >
                                <CardContent sx={{ pb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                                        <Avatar 
                                            src={teacher.photoURL} 
                                            alt={teacher.displayName} 
                                            sx={{ width: 56, height: 56, border: '1px solid', borderColor: 'divider' }} 
                                        />
                                        <Box>
                                            <Typography variant="h6" component="div" sx={{ fontSize: '1.1rem', fontWeight: 600, lineHeight: 1.2, mb: 0.5 }}>
                                                {teacher.displayName}
                                            </Typography>
                                            {/* Date and Time inline for the card view */}
                                            <Typography variant="body2" color="text.secondary">
                                                Applied: {dateTime.date} {dateTime.time && `at ${dateTime.time}`}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 0.5 }}>
                                         <Typography variant="body2" color="text.secondary">
                                            Rate: <Typography component="span" fontWeight="600" color="text.primary">
                                                {teacher.hourlyRate ? `$${teacher.hourlyRate}/hr` : 'N/A'}
                                            </Typography>
                                        </Typography>
                                    </Box>

                                    {teacher.subjects && teacher.subjects.length > 0 && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                                            {teacher.subjects.slice(0, 3).map(subject => (
                                                <Chip key={subject} label={subject} size="small" sx={{ borderRadius: 1, bgcolor: 'action.hover' }} />
                                            ))}
                                        </Box>
                                    )}
                                </CardContent>
                                <CardActions sx={{ p: 2, pt: 1 }}>
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        fullWidth
                                        endIcon={<ArrowForwardIosIcon sx={{ fontSize: '0.875rem !important' }} />}
                                        onClick={() => handleViewDetails(teacher)}
                                        sx={{ 
                                            borderRadius: 2,
                                            py: 1,
                                            textTransform: 'none',
                                            fontWeight: 600
                                        }}
                                    >
                                        Review Application
                                    </Button>
                                </CardActions>
                            </Card>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
};

export default TeacherList;